/**
 * Google Consent Mode v2 Management
 * Handles consent state and updates Google Analytics/Tag Manager consent signals
 */

const CONSENT_STORAGE_KEY = 'ojanna_consent_preferences';
const CONSENT_EXPIRY_DAYS = 365;

/**
 * Get stored consent preferences from localStorage
 */
export function getStoredConsent() {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    const expiryDate = new Date(parsed.expiry);
    
    // Check if consent has expired
    if (new Date() > expiryDate) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    
    return parsed.preferences;
  } catch (e) {
    return null;
  }
}

/**
 * Save consent preferences to localStorage
 */
export function saveConsent(preferences) {
  try {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + CONSENT_EXPIRY_DAYS);
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify({
      preferences,
      expiry: expiry.toISOString(),
    }));
  } catch (e) {
    console.error('Failed to save consent preferences:', e);
  }
}

/**
 * Update Google Consent Mode signals
 */
export function updateConsentMode(preferences) {
  if (!window.gtag) {
    console.warn('gtag not available');
    return;
  }

  const {
    analytics = false,
    advertising = false,
  } = preferences;

  // Update consent signals
  window.gtag('consent', 'update', {
    'analytics_storage': analytics ? 'granted' : 'denied',
    'ad_storage': advertising ? 'granted' : 'denied',
    'ad_user_data': advertising ? 'granted' : 'denied',
    'ad_personalization': advertising ? 'granted' : 'denied',
  });

  // Also update dataLayer for GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'consent_update',
      analytics_consent: analytics ? 'granted' : 'denied',
      advertising_consent: advertising ? 'granted' : 'denied',
    });
  }
}

/**
 * Initialize consent from stored preferences or set defaults
 */
export function initializeConsent() {
  const stored = getStoredConsent();
  
  if (stored) {
    // User has previously set preferences
    updateConsentMode(stored);
    return { preferences: stored, hasConsent: true };
  }
  
  // No stored preferences - show banner
  return { preferences: null, hasConsent: false };
}

/**
 * Accept all cookies
 */
export function acceptAll() {
  const preferences = {
    analytics: true,
    advertising: true,
  };
  
  saveConsent(preferences);
  updateConsentMode(preferences);
  
  return preferences;
}

/**
 * Accept only necessary cookies (analytics only, no advertising)
 */
export function acceptNecessary() {
  const preferences = {
    analytics: true,
    advertising: false,
  };
  
  saveConsent(preferences);
  updateConsentMode(preferences);
  
  return preferences;
}

/**
 * Reject all cookies
 */
export function rejectAll() {
  const preferences = {
    analytics: false,
    advertising: false,
  };
  
  saveConsent(preferences);
  updateConsentMode(preferences);
  
  return preferences;
}

/**
 * Custom consent preferences
 */
export function setCustomConsent(preferences) {
  saveConsent(preferences);
  updateConsentMode(preferences);
  
  return preferences;
}

/**
 * Re-open the cookie preferences panel (footer "Cookie settings", Cookie Policy page)
 */
export const COOKIE_SETTINGS_EVENT = 'ojanna:cookie-settings';

export function openCookieSettings() {
  window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
}
